import React, { useState, useCallback, useMemo } from 'react'
import * as graph from '@adc/sdk'
import type { Maybe, UserVerification } from '@adc/sdk/types'
import logger from '@adc/logger'
import classnames from 'classnames/bind'
import JumioVerification from './jumioVerification'
import VerificationInitializer from './verificationInitializer'
import IdVerificationStatus from './verificationStatus'
import {
  fetchUserVerificationStatus as fetchVerificationStatus,
  performProcessIdUploadVerification as performIdUploadVerification,
  initiateIdentityVerification as initiateIdVerification,
} from './providers'
import { Loader } from '../elementals'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const { VerificationStatus } = graph.enums

const DEFAULT_ERROR_MESSAGE =
  'There was an error connecting with our Identity Verification Vendor.'

interface IdVerificationContainerProps {
  verificationStatus: Maybe<UserVerification>
  className?: string
  showIntro?: boolean
  onContinue?: () => void
  onGoBack?: () => void
  onVerificationStatusChange: (
    verificationStatus: Maybe<UserVerification>
  ) => void
}
interface IdVerificationProps extends IdVerificationContainerProps {
  fetchUserVerificationStatus: () => Promise<Maybe<UserVerification>>
  performProcessIdUploadVerification: () => Promise<string | unknown>
  initiateIdentityVerification: () => Promise<any>
}

export const IdVerification = ({
  className,
  showIntro: showIntroProp,
  verificationStatus,
  onGoBack,
  onContinue,
  onVerificationStatusChange,
  fetchUserVerificationStatus,
  performProcessIdUploadVerification,
  initiateIdentityVerification,
}: IdVerificationProps) => {
  const redirectUrl =
    verificationStatus?.internal_verification_status ===
    VerificationStatus.VerificationInProgress
      ? verificationStatus?.redirect_url
      : ''
  const [frameUrl, setFrameUrl] = useState(redirectUrl)
  const [hasVerificationDone, setHasVerificationDone] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showIntro, setShowIntro] = useState(showIntroProp)

  const handleFetchVerificationStatus = useCallback(
    (hasVerificationDone?: boolean) => {
      setIsLoading(true)
      fetchUserVerificationStatus()
        .then(response => {
          const { internal_verification_status, redirect_url } = response || {}
          onVerificationStatusChange?.(response)
          if (
            internal_verification_status ===
              VerificationStatus.VerificationInProgress &&
            redirect_url
          ) {
            setFrameUrl(redirect_url)
          }
        })
        .catch(error => {
          logger.error('Error fetching verification status:', error)
          setError(DEFAULT_ERROR_MESSAGE)
        })
        .finally(() => {
          setIsLoading(false)
          if (hasVerificationDone) {
            setHasVerificationDone(true)
          }
        })
    },
    [
      fetchUserVerificationStatus,
      onVerificationStatusChange,
      setError,
      setFrameUrl,
      setIsLoading,
      setHasVerificationDone,
    ]
  )

  const handleInitiateIdentityVerification = useCallback(() => {
    setIsLoading(true)
    initiateIdentityVerification()
      .then(() => {
        setShowIntro(false)
        setError(null)
      })
      .catch(e => {
        const errorMessage = e?.body?.message || DEFAULT_ERROR_MESSAGE
        logger.error('Error initiating identity verification', errorMessage)
        setError(errorMessage)
      })
      .finally(() => {
        handleFetchVerificationStatus()
      })
  }, [
    showIntro,
    handleFetchVerificationStatus,
    initiateIdentityVerification,
    setFrameUrl,
    setIsLoading,
    setError,
    setShowIntro,
  ])

  const handleJumioVerification = useCallback(
    async (result?: string) => {
      if (result === 'success') {
        setIsLoading(true)
        try {
          await performProcessIdUploadVerification()
        } catch (error) {
          logger.error('Error processing ID upload verification:', error)
          setError(DEFAULT_ERROR_MESSAGE)
        }
      } else {
        setError(DEFAULT_ERROR_MESSAGE)
      }
      handleFetchVerificationStatus(true)
    },
    [
      performIdUploadVerification,
      handleFetchVerificationStatus,
      onVerificationStatusChange,
      setHasVerificationDone,
      setIsLoading,
    ]
  )

  const handleRetry = useCallback(() => {
    setHasVerificationDone(false)
    setFrameUrl('')
    handleInitiateIdentityVerification()
  }, [setFrameUrl, setHasVerificationDone, handleInitiateIdentityVerification])

  const showLoader = isLoading && !hasVerificationDone

  const content = useMemo(() => {
    if (
      !frameUrl &&
      verificationStatus?.internal_verification_status ===
        VerificationStatus.NotVerified &&
      (!error || showIntroProp)
    ) {
      return (
        <VerificationInitializer
          error={error}
          initiateIdentityVerification={handleInitiateIdentityVerification}
          onGoBack={onGoBack}
          showIntro={showIntro}
        />
      )
    } else if (
      !!frameUrl &&
      verificationStatus?.internal_verification_status ===
        VerificationStatus.VerificationInProgress &&
      !hasVerificationDone &&
      !error
    ) {
      return (
        <JumioVerification
          frameUrl={frameUrl}
          onVerify={handleJumioVerification}
        />
      )
    }
    return (
      <IdVerificationStatus
        error={error}
        isLoading={isLoading}
        verificationStatus={verificationStatus}
        fetchVerificationStatus={handleFetchVerificationStatus}
        onGoBack={onGoBack}
        onContinue={onContinue}
        onRetry={handleRetry}
      />
    )
  }, [
    frameUrl,
    error,
    hasVerificationDone,
    isLoading,
    showIntro,
    showIntroProp,
    verificationStatus,
    handleInitiateIdentityVerification,
    handleJumioVerification,
    handleFetchVerificationStatus,
    handleRetry,
    onGoBack,
    onContinue,
  ])

  return (
    <div className={cx('verification-container', className)}>
      {content}
      {showLoader && <Loader active inverted />}
    </div>
  )
}

const IdVerificationWithProviders = (props: IdVerificationContainerProps) => (
  <IdVerification
    {...props}
    initiateIdentityVerification={initiateIdVerification}
    fetchUserVerificationStatus={fetchVerificationStatus}
    performProcessIdUploadVerification={performIdUploadVerification}
  />
)

export default IdVerificationWithProviders
